import React from 'react';
import { Button } from '../../components/ui/Button';

const SECRET_PLACEHOLDER = '***configured***';

interface FormState {
  clientId: string;
  clientSecret: string;
  customerNumber: string;
  countryCode: string;
  senderId: string;
}

interface ConfigurationSectionProps {
  form: FormState;
  setForm: (next: FormState) => void;
  connected: boolean;
  lastTestedAt: string | null;
  lastTestResult: string | null;
  saving: boolean;
  testing: boolean;
  syncing: boolean;
  onTest: () => void;
  onSave: () => void;
  onRunSync: () => void;
  onDisconnect: () => void;
}

/**
 * Credential form for the Ingram Xvantage v6 connector. Extracted from
 * the old single-page IngramTab so it can live behind the new
 * Configuration sub-tab without ballooning the parent file.
 *
 * State + handlers stay owned by the parent (IngramTab) so the
 * connection-status / Run-sync / disconnect actions on the page header
 * share a single source of truth with the form actions here.
 */
export function ConfigurationSection({
  form,
  setForm,
  connected,
  lastTestedAt,
  lastTestResult,
  saving,
  testing,
  syncing,
  onTest,
  onSave,
  onRunSync,
  onDisconnect,
}: ConfigurationSectionProps) {
  return (
    <div className="mt-4">
      <div className="rounded-lg border border-slate-200 bg-white p-6 space-y-4">
        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Client ID</label>
          <input
            type="text"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={form.clientId}
            onChange={(e) => setForm({ ...form, clientId: e.target.value })}
            autoComplete="off"
            spellCheck={false}
          />
          {connected && form.clientId === SECRET_PLACEHOLDER && (
            <p className="mt-1 text-[11px] text-slate-500">
              A value is stored in Key Vault. Type a new one to rotate.
            </p>
          )}
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">Client Secret</label>
          <input
            type="password"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={form.clientSecret}
            onChange={(e) => setForm({ ...form, clientSecret: e.target.value })}
            autoComplete="off"
            spellCheck={false}
          />
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Customer Number</label>
            <input
              type="text"
              placeholder="e.g. 20-222222"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
              value={form.customerNumber}
              onChange={(e) => setForm({ ...form, customerNumber: e.target.value })}
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 mb-1">Country Code</label>
            <input
              type="text"
              maxLength={2}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 uppercase"
              value={form.countryCode}
              onChange={(e) => setForm({ ...form, countryCode: e.target.value.toUpperCase() })}
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-700 mb-1">
            Sender ID <span className="text-slate-400 font-normal">(optional)</span>
          </label>
          <input
            type="text"
            placeholder="aitryx"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
            value={form.senderId}
            onChange={(e) => setForm({ ...form, senderId: e.target.value })}
          />
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button onClick={onTest} disabled={testing || saving || syncing} variant="outline" className="inline-flex">
            {testing ? 'Testing…' : 'Test connection'}
          </Button>
          <Button onClick={onSave} disabled={saving || testing || syncing} className="inline-flex">
            {saving ? 'Saving…' : 'Save'}
          </Button>
          {connected && (
            <Button
              onClick={onRunSync}
              disabled={saving || testing || syncing}
              variant="outline"
              className="inline-flex border-blue-300 text-blue-700 hover:bg-blue-50"
            >
              {syncing ? 'Syncing…' : 'Run sync'}
            </Button>
          )}
          {connected && (
            <Button
              onClick={onDisconnect}
              disabled={saving || testing || syncing}
              variant="outline"
              className="inline-flex ml-auto border-rose-300 text-rose-700 hover:bg-rose-50"
            >
              Disconnect
            </Button>
          )}
        </div>
      </div>

      {lastTestedAt && (
        <p className="mt-3 text-[11px] text-slate-500">
          Last tested {new Date(lastTestedAt).toLocaleString()}
          {lastTestResult ? ` — ${lastTestResult}` : null}
        </p>
      )}
    </div>
  );
}
